import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { BadgeCheck, ArrowUpRight, ArrowLeft, Activity, X, Loader2, CandlestickChart, LineChart } from "lucide-react";
import { useMarkets } from "@/hooks/useMarkets";
import Sparkline from "./Sparkline";
import CandleChart, { RANGES } from "./CandleChart";
import MoneyFlow from "./MoneyFlow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "crypto", label: "Crypto" },
  { id: "stocks", label: "Stocks" },
  { id: "metals", label: "Metals" },
  { id: "forex", label: "Forex" },
];

const TAB_INFO = {
  crypto: "The top twenty-five digital assets — live spot prices, 24-hour change and seven-day trend.",
  stocks: "Twenty-five verified mega-caps — the companies that move the world index, quoted in real time.",
  metals: "Twenty metals — bullion benchmarks, base-metal futures and the strategic metals complex.",
  forex: "Twelve major currency pairs — live spot rates, around the clock, five days a week.",
};

const fmtPrice = (n) =>
  n == null ? "—"
    : n >= 1000 ? n.toLocaleString("en-US", { maximumFractionDigits: 2 })
    : n >= 1 ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : n.toPrecision(4);

const fmtMcap = (n) =>
  n == null ? "—"
    : n >= 1e12 ? `${(n / 1e12).toFixed(2)}T`
    : n >= 1e9 ? `${(n / 1e9).toFixed(1)}B`
    : `${(n / 1e6).toFixed(0)}M`;

function ChangePill({ value, testid }) {
  if (value == null) return null;
  const flat = value === 0;
  const up = value > 0;
  return (
    <span
      data-testid={testid}
      className={`font-tabular text-xs ${flat ? "text-white/40" : up ? "text-[#00FF41]" : "text-[#FF003C]"}`}
    >
      {flat ? "•" : up ? "▲" : "▼"} {Math.abs(value).toFixed(2)}%
    </span>
  );
}

function ImgLogo({ src, alt, fallback }) {
  const [failed, setFailed] = useState(false);
  if (failed || !src) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-gold/30 bg-gold/[0.06] font-tabular text-[10px] text-gold">
        {fallback}
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-8 w-8 shrink-0 rounded-full border border-white/10 bg-white/95 object-contain p-1"
    />
  );
}

function RowShell({ children, i, testid, onSelect }) {
  return (
    <motion.button
      data-testid={testid}
      onClick={onSelect}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(i, 12) * 0.03 }}
      className="flex w-full cursor-pointer items-center justify-between gap-4 border-t border-white/10 px-4 py-3.5 text-left transition-colors duration-300 first:border-t-0 hover:bg-white/[0.03] sm:px-6"
    >
      {children}
    </motion.button>
  );
}

function CryptoRows({ coins, onSelect }) {
  return coins.map((c, i) => {
    const chg = c.change_24h;
    return (
      <RowShell key={c.id} i={i} testid={`markets-row-crypto-${c.symbol}`}
        onSelect={() => onSelect({ symbol: c.symbol.toUpperCase(), name: c.name, kind: "crypto", price: c.price, chg, logo: c.logo })}>
        <span className="flex min-w-0 items-center gap-3">
          <span className="hidden w-6 text-right font-tabular text-[10px] text-white/30 sm:block">
            {String(i + 1).padStart(2, "0")}
          </span>
          <ImgLogo
            src={c.logo || `https://assets.coincap.io/assets/icons/${String(c.symbol).toLowerCase()}@2x.png`}
            alt={c.name}
            fallback={c.symbol.slice(0, 2).toUpperCase()}
          />
          <span className="min-w-0">
            <span className="block truncate text-sm text-white">{c.name}</span>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-gold/70">{c.symbol}</span>
          </span>
        </span>
        <span className="hidden md:block">
          <Sparkline data={c.sparkline_7d || c.sparkline} positive={(chg ?? 0) >= 0} />
        </span>
        <span className="flex items-baseline gap-4">
          <span className="hidden text-right font-tabular text-[11px] text-white/35 lg:block">
            {fmtMcap(c.market_cap)}
          </span>
          <span className="font-tabular text-sm text-white/85" data-testid={`markets-price-${c.symbol}`}>
            ${fmtPrice(c.price)}
          </span>
          <ChangePill value={chg} testid={`markets-change-${c.symbol}`} />
        </span>
      </RowShell>
    );
  });
}

function StockRows({ rows, onSelect }) {
  return rows.map((s, i) => (
    <RowShell key={s.symbol} i={i} testid={`markets-row-stock-${s.symbol.toLowerCase()}`}
      onSelect={() => onSelect({ symbol: s.symbol, name: s.name, kind: "stock", price: s.price, chg: s.change_pct, logo: s.logo })}>
      <span className="flex min-w-0 items-center gap-3">
        <ImgLogo src={s.logo} alt={s.name} fallback={s.symbol.slice(0, 2)} />
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm text-white">
            {s.symbol}
            {s.verified && (
              <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-gold" data-testid={`markets-verified-${s.symbol.toLowerCase()}`} />
            )}
          </span>
          <span className="block truncate text-[10px] text-white/45">{s.name}</span>
        </span>
      </span>
      <span className="flex items-baseline gap-4">
        <span className="font-tabular text-sm text-white/85" data-testid={`markets-price-${s.symbol.toLowerCase()}`}>
          ${fmtPrice(s.price)}
        </span>
        <ChangePill value={s.change_pct} testid={`markets-change-${s.symbol.toLowerCase()}`} />
      </span>
    </RowShell>
  ));
}

function MetalRows({ rows, onSelect }) {
  return rows.map((m, i) => (
    <RowShell key={m.symbol} i={i} testid={`markets-row-metal-${m.symbol.toLowerCase()}`}
      onSelect={() => onSelect({ symbol: m.symbol, name: m.name, kind: "metal", price: m.price, chg: m.change_pct, badge: m.badge })}>
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-gold/40 bg-gold/[0.07] font-tabular text-[10px] font-medium text-gold">
          {m.badge}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 text-sm text-gold">
            {m.symbol}
            <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-gold" data-testid={`markets-verified-${m.symbol.toLowerCase()}`} />
          </span>
          <span className="block truncate text-[10px] uppercase tracking-[0.2em] text-white/45">{m.name}</span>
        </span>
      </span>
      <span className="flex items-baseline gap-4">
        <span className="hidden text-[10px] uppercase tracking-[0.15em] text-white/30 sm:block">{m.unit}</span>
        <span className="font-tabular text-sm text-white/85" data-testid={`markets-price-${m.symbol.toLowerCase()}`}>
          ${fmtPrice(m.price)}
        </span>
        <ChangePill value={m.change_pct} testid={`markets-change-${m.symbol.toLowerCase()}`} />
      </span>
    </RowShell>
  ));
}

export function FlagPair({ flags, symbol }) {
  const ok = flags && flags[0] && flags[1];
  if (!ok) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-white/15 bg-white/[0.04] font-tabular text-[10px] text-white/70">
        {symbol.slice(0, 3)}
      </span>
    );
  }
  return (
    <span className="flex h-8 w-11 shrink-0 items-center" data-testid={`fx-flags-${symbol.toLowerCase()}`}>
      <img
        src={`https://flagcdn.com/w40/${flags[0]}.png`}
        alt={flags[0].toUpperCase()}
        loading="lazy"
        className="h-6 w-6 rounded-full border border-white/25 object-cover"
      />
      <img
        src={`https://flagcdn.com/w40/${flags[1]}.png`}
        alt={flags[1].toUpperCase()}
        loading="lazy"
        className="-ml-2 h-6 w-6 rounded-full border border-white/25 object-cover"
      />
    </span>
  );
}

function ForexRows({ rows, onSelect }) {
  return rows.map((f, i) => (
    <RowShell key={f.symbol} i={i} testid={`markets-row-fx-${f.symbol.toLowerCase()}`}
      onSelect={() => onSelect({ symbol: f.symbol, name: f.name, kind: "fx", price: f.price, chg: f.change_pct, flags: f.flags })}>
      <span className="flex min-w-0 items-center gap-3">
        <FlagPair flags={f.flags} symbol={f.symbol} />
        <span className="min-w-0">
          <span className="block text-sm text-white">{f.symbol}</span>
          <span className="block truncate text-[10px] uppercase tracking-[0.2em] text-white/45">{f.name}</span>
        </span>
      </span>
      <span className="flex items-baseline gap-4">
        <span className="hidden text-[10px] uppercase tracking-[0.15em] text-white/30 sm:block">spot</span>
        <span className="font-tabular text-sm text-white/85" data-testid={`markets-price-${f.symbol.toLowerCase()}`}>
          {f.price?.toFixed(4) ?? "—"}
        </span>
      </span>
    </RowShell>
  ));
}

export default function Markets() {
  const { coins, macro, stocks, updatedAt } = useMarkets();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "crypto";
  const active = TABS.some((t) => t.id === tab) ? tab : "crypto";

  useEffect(() => {
    if (tab !== active) setParams({ tab: active }, { replace: true });
  }, [tab, active, setParams]);

  useEffect(() => {
    document.title = "Aureon Global Markets — Live Markets";
    window.scrollTo(0, 0);
  }, []);

  const metals = useMemo(() => macro.filter((r) => r.kind === "metal"), [macro]);
  const forex = useMemo(() => macro.filter((r) => r.kind === "fx"), [macro]);
  const counts = { crypto: coins.length, stocks: stocks.length, metals: metals.length, forex: forex.length };

  // Chart drawer
  const [chartSel, setChartSel] = useState(null);
  const [chartRange, setChartRange] = useState("1d");
  const [chartMode, setChartMode] = useState("candles");
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartTab, setChartTab] = useState("chart");

  useEffect(() => {
    if (!chartSel) return undefined;
    let cancelled = false;
    const load = (showSpinner) => {
      if (showSpinner) setChartLoading(true);
      axios
        .get(`${API}/markets/chart/${chartSel.symbol}?span=${chartRange}`)
        .then(({ data }) => { if (!cancelled) setChartData(data); })
        .catch(() => { if (!cancelled) setChartData(null); })
        .finally(() => { if (!cancelled && showSpinner) setChartLoading(false); });
    };
    load(true);
    const id = setInterval(() => {
      if (document.visibilityState === "visible") load(false);
    }, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [chartSel, chartRange]);
  return (
    <div data-testid="markets-page" className="min-h-screen bg-ink text-white">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="markets-back-link" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-gold">
            <ArrowLeft className="h-3.5 w-3.5" />
            Aureon
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/terminal"
              data-testid="markets-terminal-cta"
              className="flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink"
            >
              Open terminal
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-24 pt-14 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <p className="mb-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold">
            <Activity className="h-3 w-3" />
            Live markets
          </p>
          <h1 className="font-serif text-5xl font-light tracking-tighter text-white sm:text-6xl">
            Four arenas.
            <span className="italic text-gold"> One board.</span>
          </h1>
          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/50" data-testid="markets-tab-info">
            {TAB_INFO[active]}
          </p>
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-2" data-testid="markets-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`markets-tab-${t.id}`}
              onClick={() => setParams({ tab: t.id })}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                active === t.id
                  ? "bg-gold text-ink"
                  : "border border-white/15 text-white/60 hover:border-gold/50 hover:text-gold"
              }`}
            >
              {t.label}
              <span className={`font-tabular text-[10px] ${active === t.id ? "text-ink/60" : "text-white/30"}`}>
                {counts[t.id] || "—"}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-8 border border-white/10 bg-[#141414]/40 backdrop-blur-xl" data-testid="markets-table">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {active === "crypto" && (coins.length ? <CryptoRows coins={coins} onSelect={setChartSel} /> : <Empty />)}
              {active === "stocks" && (stocks.length ? <StockRows rows={stocks} onSelect={setChartSel} /> : <Empty />)}
              {active === "metals" && (metals.length ? <MetalRows rows={metals} onSelect={setChartSel} /> : <Empty />)}
              {active === "forex" && (forex.length ? <ForexRows rows={forex} onSelect={setChartSel} /> : <Empty />)}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
            {updatedAt ? `Updated ${new Date(updatedAt * 1000).toLocaleTimeString()}` : "Loading live data…"}
          </p>
          <Link to="/#access" data-testid="markets-access-cta" className="text-[11px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-light">
            Get terminal access ↗
          </Link>
        </div>
      </main>

      <AnimatePresence>
        {chartSel && (
          <motion.div
            data-testid="markets-chart-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
            onClick={() => setChartSel(null)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-3xl border border-white/10 bg-[#111111] p-6 sm:p-8"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  {chartSel.logo ? (
                    <ImgLogo src={chartSel.logo} alt={chartSel.name} fallback={chartSel.symbol.slice(0, 2)} />
                  ) : chartSel.badge ? (
                    <span className="flex h-8 w-8 items-center justify-center border border-gold/40 bg-gold/[0.07] font-tabular text-[10px] font-medium text-gold">
                      {chartSel.badge}
                    </span>
                  ) : (
                    <FlagPair flags={chartSel.flags} symbol={chartSel.symbol} />
                  )}
                  <div>
                    <p className="flex items-center gap-1.5 text-sm text-white" data-testid="chart-modal-symbol">
                      {chartSel.symbol}
                      {(chartSel.kind === "stock" || chartSel.kind === "metal") && (
                        <BadgeCheck className="h-3.5 w-3.5 text-gold" />
                      )}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">{chartSel.name}</p>
                  </div>
                </div>
                <button
                  data-testid="chart-modal-close"
                  onClick={() => setChartSel(null)}
                  className="border border-white/15 p-2 text-white/50 transition-colors hover:border-gold hover:text-gold"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 flex items-baseline gap-3">
                <p className="font-serif text-3xl font-light tracking-tight text-white" data-testid="chart-modal-price">
                  {chartSel.kind === "fx" ? chartSel.price?.toFixed(4) : `$${fmtPrice(chartSel.price)}`}
                </p>
                <ChangePill value={chartSel.chg} testid="chart-modal-change" />
              </div>

              <div className="mt-4 flex gap-2 border-b border-white/10">
                <button
                  data-testid="chart-tab-chart"
                  onClick={() => setChartTab("chart")}
                  className={`-mb-px px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors ${
                    chartTab === "chart" ? "border-b border-gold text-gold" : "text-white/45 hover:text-white/80"
                  }`}
                >
                  Chart
                </button>
                <button
                  data-testid="chart-tab-flow"
                  onClick={() => setChartTab("flow")}
                  className={`-mb-px px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors ${
                    chartTab === "flow" ? "border-b border-gold text-gold" : "text-white/45 hover:text-white/80"
                  }`}
                >
                  Data · Money flow
                </button>
              </div>

              {chartTab === "chart" ? (
                <>
                <div className="relative mt-4">
                  {chartLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#111111]/60">
                      <Loader2 className="h-5 w-5 animate-spin text-gold" />
                    </div>
                  )}
                  <CandleChart candles={chartData?.candles} mode={chartMode} prefix={chartSel.kind === "fx" ? "" : "$"} />
                </div>

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex gap-2">
                    <button
                      data-testid="chart-mode-line"
                      onClick={() => setChartMode("line")}
                      className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${
                        chartMode === "line" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:text-gold"
                      }`}
                    >
                      <LineChart className="h-3.5 w-3.5" /> Line
                    </button>
                    <button
                      data-testid="chart-mode-candles"
                      onClick={() => setChartMode("candles")}
                      className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs transition-colors duration-300 ${
                        chartMode === "candles" ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:text-gold"
                      }`}
                    >
                      <CandlestickChart className="h-3.5 w-3.5" /> Candles
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {RANGES.map((r) => (
                      <button
                        key={r.k}
                        data-testid={`chart-range-${r.l}`}
                        onClick={() => setChartRange(r.k)}
                        className={`rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] transition-colors duration-300 ${
                          chartRange === r.k ? "bg-white/15 text-gold" : "text-white/45 hover:text-gold"
                        }`}
                      >
                        {r.l}
                      </button>
                    ))}
                  </div>
                </div>
                </>
              ) : (
                <div className="mt-6">
                  <MoneyFlow symbol={chartSel.symbol} />
                </div>
              )}

              <Link
                to="/terminal"
                data-testid="chart-modal-terminal-cta"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light"
              >
                Trade in the terminal
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Empty() {
  return (
    <div className="px-6 py-16 text-center text-xs uppercase tracking-[0.25em] text-white/40" data-testid="markets-empty">
      Streaming live data…
    </div>
  );
}
